import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  DATE_FIELDS,
  DOCUMENT_FIELDS,
  DOCUMENT_FIELD_LABEL,
  DOCUMENT_KIND_LABEL,
  DOCUMENT_KINDS,
  DEFAULT_DRUM_L,
  DEFAULT_MR_WARN_H,
  NUMBER_FIELDS,
  documentChecks,
  normaliseValues,
  valuesAsText,
  valuesFromText,
  type DocumentCheck,
  type DocumentCheckContext,
  type DocumentCheckState,
  type DocumentReadOutcome,
  type DocumentScan,
  type MrLogRowText,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { ErrorState, Loading } from '../components/States.tsx';
import { readConfig } from '../config.ts';
import type { PortalProject } from './api.ts';
import { browserPhotoRenderer } from './photoImages.ts';
import {
  addDocumentPicture,
  documentPictureUrl,
  loadDocumentsData,
  readDocument,
  removeDocumentPicture,
  saveDocumentValues,
  type DocumentsData,
} from './documentsApi.ts';
import { TableWrap } from '../components/TableWrap.tsx';

/** Roles that add pictures and save their values (office staff read only, decision 507's writers). */
const WRITERS = ['tenant_admin', 'manager', 'field_crew', 'data_processor'];

const QC: Readonly<Record<DocumentCheckState, string>> = {
  pass: 'chip chip--pass',
  warn: 'chip chip--warn',
  fail: 'chip chip--fail',
  info: 'chip',
};
const STATE_WORD: Readonly<Record<DocumentCheckState, string>> = {
  pass: 'Matches',
  warn: 'Check',
  fail: 'Does not match',
  info: 'Not checked yet',
};

export interface DocumentsServices {
  readonly read: (scan: DocumentScan, bytes: Uint8Array) => Promise<DocumentReadOutcome>;
  /** The picture's bytes from its signed link. */
  readonly download: (url: string) => Promise<Uint8Array>;
  /** A chosen bitmap as a PNG (R144; the browser's canvas; tests inject a fake, jsdom has no canvas). */
  readonly toPng: (file: Blob) => Promise<Blob>;
  readonly today: () => string;
  /** Who checks the values: the signed-in person's email. */
  readonly who: () => Promise<string>;
}

export interface DocumentsPanelProps {
  readonly sb: SupabaseClient;
  readonly project: Pick<PortalProject, 'id' | 'tenantId' | 'number'>;
  readonly roles: readonly string[];
  readonly services?: Partial<DocumentsServices>;
}

function Checks({ checks }: { readonly checks: readonly DocumentCheck[] }): ReactElement | null {
  if (checks.length === 0) return null;
  return (
    <ul className="doc-checks" aria-label="Checks">
      {checks.map((c) => (
        <li key={c.key} className="body">
          <span className={QC[c.state]}>{c.word}</span> {c.message}
        </li>
      ))}
    </ul>
  );
}

/**
 * The staff project tab "Fuel and MR", its first part (F03, F09; docs/specs/document-reading.md §6): the project's
 * MR pages, fuel dockets and receipts with their checks, "Add a photo or scan", and the checking panel with "Read
 * from the photo". Clients never see it (RLS refuses them).
 */
export function DocumentsPanel({ sb, project, roles, services }: DocumentsPanelProps): ReactElement {
  const s: DocumentsServices = {
    read: (scan, bytes) => {
      const c = readConfig();
      return readDocument(sb, c.supabaseUrl, c.supabasePublishableKey, scan, bytes);
    },
    download: async (url) => new Uint8Array(await (await fetch(url)).arrayBuffer()),
    toPng: (file) => browserPhotoRenderer.toPng(file),
    today: () => new Date().toLocaleDateString('en-CA'),
    who: async () => (await sb.auth.getSession()).data.session?.user.email ?? '',
    ...services,
  };
  const canWrite = roles.some((r) => WRITERS.includes(r));
  const [data, setData] = useState<DocumentsData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const [adding, setAdding] = useState<{
    kind: DocumentScan['kind'];
    aircraftId: string;
    recordId: string;
    file: File | null;
  }>({
    kind: 'mr_front',
    aircraftId: '',
    recordId: '',
    file: null,
  });
  const [open, setOpen] = useState<{
    scan: DocumentScan;
    url: string | null;
    bytes: Uint8Array | null;
  } | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [rows, setRows] = useState<MrLogRowText[]>([]);
  const [reading, setReading] = useState<{
    values: unknown;
    unsure: readonly string[];
    model: string;
  } | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => {
    setError(null);
    loadDocumentsData(sb, project)
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [sb, project]);
  useEffect(reload, [reload]);

  const ctx: DocumentCheckContext | null = useMemo(
    () =>
      data && {
        aircraft: data.aircraft,
        entries: data.entries,
        fuel: data.fuel,
        today: s.today(),
        warnH: data.mrWarnH ?? DEFAULT_MR_WARN_H,
        drumL: data.drumL ?? DEFAULT_DRUM_L,
      },
    // the clock is read once per load
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data],
  );

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data || !ctx) return <Loading what="maintenance release pages and dockets" />;

  const rego = (id: string | null) => data.aircraft.find((a) => a.id === id)?.registration ?? '–';
  const recordWords = (scan: DocumentScan): string => {
    if (scan.kind === 'fuel_docket') {
      const f = data.fuel.find((x) => x.id === scan.fuel_entry_id);
      return f ? `Resupply ${f.date} ${f.location}` : 'Resupply';
    }
    if (scan.kind === 'fuel_receipt') {
      const e = data.entries.find((x) => x.id === scan.daily_entry_id);
      return e ? `Refuel ${e.date}${e.flt_num ? ` flt ${e.flt_num}` : ''}, ${rego(e.aircraft_id)}` : 'Refuel';
    }
    return rego(scan.aircraft_id);
  };
  const resultOf = (scan: DocumentScan) =>
    Object.keys(scan.checked_values).length === 0
      ? null
      : documentChecks(scan, normaliseValues(scan.kind, scan.checked_values), ctx);

  async function show(scan: DocumentScan, bytes: Uint8Array | null = null): Promise<void> {
    setNotice(null);
    setReading(null);
    const t = valuesAsText(scan.kind, scan.checked_values);
    setFields({ ...t.fields });
    setRows([...t.rows]);
    let url: string | null = null;
    if (data!.uploaded.has(scan.id) || bytes) {
      try {
        url = await documentPictureUrl(sb, project, scan);
      } catch {
        url = null;
      }
    }
    setOpen({ scan, url, bytes });
  }

  async function add(): Promise<void> {
    if (!adding.file) return setNotice({ ok: false, text: 'Choose a photo or scan.' });
    const needsAircraft = adding.kind === 'mr_front' || adding.kind === 'mr_log';
    if (needsAircraft && !adding.aircraftId) return setNotice({ ok: false, text: 'Choose the aircraft.' });
    if (!needsAircraft && !adding.recordId)
      return setNotice({
        ok: false,
        text: adding.kind === 'fuel_docket' ? 'Choose the resupply.' : 'Choose the refuel.',
      });
    setBusy(true);
    try {
      const entry =
        adding.kind === 'fuel_receipt' ? data!.entries.find((e) => e.id === adding.recordId) : undefined;
      const { scan, bytes } = await addDocumentPicture(
        sb,
        project,
        {
          kind: adding.kind,
          aircraftId: needsAircraft ? adding.aircraftId : (entry?.aircraft_id ?? null),
          fuelEntryId: adding.kind === 'fuel_docket' ? adding.recordId : null,
          dailyEntryId: adding.kind === 'fuel_receipt' ? adding.recordId : null,
          file: adding.file,
        },
        undefined,
        s.toPng,
      );
      setData({ ...data!, scans: [...data!.scans, scan], uploaded: new Set([...data!.uploaded, scan.id]) });
      setAdding({ ...adding, file: null });
      await show(scan, bytes);
    } catch (e) {
      setNotice({ ok: false, text: `Not added. ${e instanceof Error ? e.message : String(e)}` });
    } finally {
      setBusy(false);
    }
  }

  const live = data.scans.filter((x) => x.deleted_at === null);
  const openValues = open ? valuesFromText(open.scan.kind, { fields, rows }) : null;
  const openResult = open && openValues ? documentChecks(open.scan, openValues, ctx) : null;

  async function read(): Promise<void> {
    if (!open) return;
    setBusy(true);
    setNotice({ ok: true, text: 'Reading the picture…' });
    try {
      const bytes = open.bytes ?? (open.url ? await s.download(open.url) : null);
      if (!bytes) return setNotice({ ok: false, text: 'The picture is not in Teslyn Cloud yet.' });
      const out = await s.read(open.scan, bytes);
      if (out.kind !== 'read') return setNotice({ ok: false, text: out.message });
      const t = valuesAsText(open.scan.kind, out.reading.values);
      setFields({ ...t.fields });
      setRows([...t.rows]);
      setReading({ values: out.reading.values, unsure: out.reading.unsure, model: out.model });
      setNotice({ ok: true, text: 'Read. Check every value against the picture, then save.' });
    } catch (e) {
      setNotice({ ok: false, text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  async function save(): Promise<void> {
    if (!open || !openValues) return;
    setBusy(true);
    try {
      const saved = await saveDocumentValues(sb, {
        id: open.scan.id,
        checked_values: openValues as unknown as Record<string, unknown>,
        read_values: reading ? { values: reading.values, unsure: reading.unsure } : open.scan.read_values,
        read_by: reading ? reading.model : open.scan.read_by,
        read_at: reading ? new Date().toISOString() : open.scan.read_at,
        checked_by_name: await s.who(),
        checked_at: new Date().toISOString(),
      });
      setData({ ...data!, scans: data!.scans.map((x) => (x.id === saved.id ? saved : x)) });
      setOpen({ ...open, scan: saved });
      setNotice({ ok: true, text: 'Checked values saved.' });
    } catch (e) {
      setNotice({ ok: false, text: `Not saved. ${e instanceof Error ? e.message : String(e)}` });
    } finally {
      setBusy(false);
    }
  }

  async function remove(): Promise<void> {
    if (!open) return;
    try {
      await removeDocumentPicture(sb, open.scan.id);
      setData({ ...data!, scans: data!.scans.filter((x) => x.id !== open.scan.id) });
      setOpen(null);
    } catch (e) {
      setNotice({ ok: false, text: `Not removed. ${e instanceof Error ? e.message : String(e)}` });
    }
  }

  const unsure = reading?.unsure ?? [];
  const records =
    adding.kind === 'fuel_docket'
      ? data.fuel
          .filter((f) => f.kind === 'resupply')
          .map((f) => [f.id, `${f.date} ${f.time} ${f.location}`] as const)
      : data.entries
          .filter((e) => e.fuel_load_l > 0)
          .sort((a, b) => b.date.localeCompare(a.date))
          .map(
            (e) =>
              [
                e.id,
                `${e.date}${e.flt_num ? ` flt ${e.flt_num}` : ''} ${rego(e.aircraft_id)} ${e.fuel_load_l} L`,
              ] as const,
          );

  return (
    <div className="stack">
      <h2 className="heading">Maintenance release pages, dockets and receipts</h2>
      <p className="caption muted">
        Photos and scans of the MR pages, fuel dockets and receipts, from Teslyn Field, the phone or added
        here, each checked against the project's flights and fuel records.
      </p>
      {notice && !open && (
        <p
          className={notice.ok ? 'caption muted' : 'notice notice--fail'}
          role={notice.ok ? 'status' : 'alert'}
        >
          {notice.text}
        </p>
      )}
      {canWrite && (
        <div className="panel stack" role="group" aria-label="Add a photo or scan">
          <div className="form-grid">
            <label className="field">
              <span className="label">Document</span>
              <select
                className="input"
                value={adding.kind}
                onChange={(e) =>
                  setAdding({ ...adding, kind: e.target.value as DocumentScan['kind'], recordId: '' })
                }
              >
                {DOCUMENT_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {DOCUMENT_KIND_LABEL[k]}
                  </option>
                ))}
              </select>
            </label>
            {adding.kind === 'mr_front' || adding.kind === 'mr_log' ? (
              <label className="field">
                <span className="label">Aircraft</span>
                <select
                  className="input"
                  value={adding.aircraftId}
                  onChange={(e) => setAdding({ ...adding, aircraftId: e.target.value })}
                >
                  <option value="">–</option>
                  {data.aircraft.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.registration}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <label className="field">
                <span className="label">{adding.kind === 'fuel_docket' ? 'Resupply' : 'Refuel'}</span>
                <select
                  className="input"
                  value={adding.recordId}
                  onChange={(e) => setAdding({ ...adding, recordId: e.target.value })}
                >
                  <option value="">–</option>
                  {records.map(([id, words]) => (
                    <option key={id} value={id}>
                      {words}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="field">
              <span className="label">Photo or PDF scan</span>
              <input
                className="input"
                type="file"
                accept=".jpg,.jpeg,.png,.webp,.bmp,.pdf"
                onChange={(e) => setAdding({ ...adding, file: e.target.files?.[0] ?? null })}
              />
            </label>
          </div>
          <div className="row">
            <Button variant="primary" busy={busy} onClick={() => void add()}>
              Add the picture
            </Button>
          </div>
        </div>
      )}
      {live.length === 0 ? (
        <div className="panel">
          <p className="body">No MR pages, dockets or receipts yet.</p>
        </div>
      ) : (
        <TableWrap>
          <table className="table" aria-label={`MR pages, dockets and receipts of ${project.number}`}>
            <thead>
              <tr>
                <th className="label" scope="col">
                  Document
                </th>
                <th className="label" scope="col">
                  For
                </th>
                <th className="label" scope="col">
                  Added
                </th>
                <th className="label" scope="col">
                  Checks
                </th>
                <th className="label" scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {[...live].reverse().map((x) => {
                const r = resultOf(x);
                return (
                  <tr key={x.id}>
                    <td>{DOCUMENT_KIND_LABEL[x.kind]}</td>
                    <td className="data">{recordWords(x)}</td>
                    <td className="caption">
                      {x.created_at.slice(0, 10)}
                      {x.added_by_name ? `, ${x.added_by_name}` : ''}
                    </td>
                    <td className="caption">
                      {r ? (
                        <>
                          <span className={QC[r.state]}>{STATE_WORD[r.state]}</span>{' '}
                          {r.headline.replace(/^[^:]+: /, '')}
                        </>
                      ) : (
                        <span className={QC.info}>{STATE_WORD.info}</span>
                      )}
                    </td>
                    <td className="table__actions">
                      <Button
                        variant="quiet"
                        aria-label={`Open ${DOCUMENT_KIND_LABEL[x.kind]} of ${x.created_at.slice(0, 10)}`}
                        onClick={() => void show(x)}
                      >
                        Open
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableWrap>
      )}

      {open && openResult && (
        <div className="panel stack" role="group" aria-label={`Check ${DOCUMENT_KIND_LABEL[open.scan.kind]}`}>
          <div className="row row--between">
            <h3 className="heading">
              {DOCUMENT_KIND_LABEL[open.scan.kind]}, {recordWords(open.scan)}
            </h3>
            <Button variant="quiet" onClick={() => setOpen(null)}>
              Close
            </Button>
          </div>
          <div className="doc-panel">
            <div className="doc-panel__picture">
              {!open.url ? (
                <p className="caption muted">
                  The picture has not reached Teslyn Cloud yet: it uploads after Teslyn Field syncs.
                </p>
              ) : open.scan.file_ext === 'pdf' ? (
                <object
                  data={open.url}
                  type="application/pdf"
                  aria-label={open.scan.file_name}
                  className="doc-panel__pdf"
                >
                  <a href={open.url} target="_blank" rel="noreferrer">
                    Open the PDF
                  </a>
                </object>
              ) : (
                <img
                  src={open.url}
                  alt={`${DOCUMENT_KIND_LABEL[open.scan.kind]} as photographed`}
                  className="doc-panel__img"
                />
              )}
            </div>
            <div className="stack">
              {canWrite && (
                <div className="row row--wrap">
                  <Button busy={busy} disabled={!open.url && !open.bytes} onClick={() => void read()}>
                    Read from the photo
                  </Button>
                  <Button variant="primary" busy={busy} onClick={() => void save()}>
                    Save checked values
                  </Button>
                  <Button variant="quiet" onClick={() => void remove()}>
                    Remove picture
                  </Button>
                </div>
              )}
              {notice && (
                <p
                  className={notice.ok ? 'caption muted' : 'notice notice--fail'}
                  role={notice.ok ? 'status' : 'alert'}
                >
                  {notice.text}
                </p>
              )}
              <div className="form-grid">
                {DOCUMENT_FIELDS[open.scan.kind]
                  .filter((k) => k !== 'rows')
                  .map((k) => (
                    <label key={k} className="field">
                      <span className="label">
                        {DOCUMENT_FIELD_LABEL[k]}
                        {unsure.includes(k) ? <span className="chip chip--warn"> Check this</span> : null}
                      </span>
                      {k === 'unit' || k === 'fuel_type' ? (
                        <select
                          className="input"
                          disabled={!canWrite}
                          value={fields[k] ?? ''}
                          onChange={(e) => setFields({ ...fields, [k]: e.target.value })}
                        >
                          <option value="">–</option>
                          {(k === 'unit'
                            ? [
                                ['L', 'Litres'],
                                ['USG', 'US gallons'],
                              ]
                            : [
                                ['avgas', 'AVGAS'],
                                ['turbine', 'AVTUR (Jet A-1)'],
                              ]
                          ).map(([v, w]) => (
                            <option key={v} value={v}>
                              {w}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          className={
                            NUMBER_FIELDS.includes(k) || DATE_FIELDS.includes(k) ? 'input data' : 'input'
                          }
                          type={DATE_FIELDS.includes(k) ? 'date' : 'text'}
                          inputMode={NUMBER_FIELDS.includes(k) ? 'decimal' : undefined}
                          spellCheck={false}
                          disabled={!canWrite}
                          value={fields[k] ?? ''}
                          onChange={(e) => setFields({ ...fields, [k]: e.target.value })}
                        />
                      )}
                    </label>
                  ))}
              </div>
              {open.scan.kind === 'mr_log' && (
                <TableWrap>
                  <table className="table" aria-label="Daily rows">
                    <thead>
                      <tr>
                        <th className="label">Date</th>
                        <th className="label">Flight time (h)</th>
                        <th className="label">Progressive total (h)</th>
                        <th className="label">Check</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => {
                        const rc = openResult.log?.rows[i];
                        const shown = rc?.checks.find((c) => c.state === rc.state);
                        return (
                          <tr key={i}>
                            {(['date', 'flight_hours', 'progressive_hours'] as const).map((k) => (
                              <td key={k}>
                                <input
                                  aria-label={`Row ${i + 1} ${k === 'date' ? 'date' : k === 'flight_hours' ? 'flight time' : 'progressive total'}`}
                                  className="input data"
                                  type={k === 'date' ? 'date' : 'text'}
                                  inputMode={k === 'date' ? undefined : 'decimal'}
                                  disabled={!canWrite}
                                  value={r[k]}
                                  onChange={(e) =>
                                    setRows(rows.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)))
                                  }
                                />
                                {unsure.includes(`rows.${i}.${k}`) && (
                                  <span className="chip chip--warn"> Check this</span>
                                )}
                              </td>
                            ))}
                            <td className="caption">
                              {shown && (
                                <>
                                  <span className={QC[rc!.state]}>{shown.word}</span> {shown.message}
                                </>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {canWrite && (
                    <Button
                      variant="quiet"
                      onClick={() =>
                        setRows([...rows, { date: '', flight_hours: '', progressive_hours: '' }])
                      }
                    >
                      Add row
                    </Button>
                  )}
                </TableWrap>
              )}
              <Checks checks={openResult.log ? openResult.log.checks : openResult.checks} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
